import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle, Clock, AlertTriangle, Plus, Filter, FileSpreadsheet, Upload, Paperclip, UserCheck, X } from 'lucide-react';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import StatusBadge from '../../components/ui/StatusBadge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious
} from '../../components/ui/pagination';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
} from '../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useToast } from '../../components/ui/use-toast';
import { motion } from 'framer-motion';

type DocumentStatus = 'pending' | 'approved' | 'rejected';
type OnboardingStatus = 'pending' | 'in-progress' | 'completed' | 'rejected' | 'approval_pending';

interface OnboardingItem {
  id: string;
  name: string;
  completed: boolean;
  documentRequired?: boolean;
  document?: {
    fileName: string;
    uploadDate: string;
    status: DocumentStatus;
    approvalDate?: string;
    approvedBy?: string;
  } | null;
}

interface OnboardingRecord {
  id: string;
  employeeId: string;
  name: string;
  email: string;
  department: string;
  designation: string;
  joiningDate: string;
  status: OnboardingStatus;
  items: OnboardingItem[];
}

// Generate sample data
const generateOnboardingData = (): OnboardingRecord[] => {
  const departments = ['Faculty', 'Administration', 'Technical', 'Marketing', 'HR'];
  const designations = [
    'Python Instructor', 'MERN Stack Instructor', 'Java Instructor', 
    'Office Admin', 'System Administrator', 'Digital Marketing Specialist',
    'HR Executive', 'Course Coordinator', 'Placement Officer'
  ];
  
  const onboardingItems: OnboardingItem[] = [
    { id: '1', name: 'Employment Contract Signed', completed: false, documentRequired: true },
    { id: '2', name: 'ID Proof Submitted', completed: false, documentRequired: true },
    { id: '3', name: 'Address Proof Submitted', completed: false, documentRequired: true },
    { id: '4', name: 'Bank Details Submitted', completed: false, documentRequired: true },
    { id: '5', name: 'Equipment Issued', completed: false },
    { id: '6', name: 'System Access Granted', completed: false },
    { id: '7', name: 'Orientation Session', completed: false },
  ];
  
  const records: OnboardingRecord[] = [];
  for (let i = 1; i <= 20; i++) {
    const department = departments[Math.floor(Math.random() * departments.length)];
    const designation = designations[Math.floor(Math.random() * designations.length)];
    
    // Randomize status and completed items
    let status: OnboardingStatus;
    const random = Math.random();
    
    if (random < 0.3) {
      status = 'pending';
    } else if (random < 0.6) {
      status = 'in-progress';
    } else if (random < 0.7) {
      status = 'approval_pending';
    } else if (random < 0.9) {
      status = 'completed';
    } else {
      status = 'rejected';
    }
    
    // Create items with random completion status based on overall status
    const items = onboardingItems.map(item => {
      let completed = false;
      let document = null;
      
      if (status === 'completed') {
        completed = true;
        if (item.documentRequired) {
          document = {
            fileName: `${item.name.toLowerCase().replace(/ /g, '_')}_employee${i}.pdf`,
            uploadDate: new Date().toISOString().split('T')[0],
            status: 'approved' as DocumentStatus,
            approvalDate: new Date().toISOString().split('T')[0],
            approvedBy: 'Admin User'
          };
        }
      } else if (status === 'approval_pending') {
        completed = item.id <= '4';
        if (item.documentRequired) {
          document = {
            fileName: `${item.name.toLowerCase().replace(/ /g, '_')}_employee${i}.pdf`,
            uploadDate: new Date().toISOString().split('T')[0],
            status: 'pending' as DocumentStatus
          };
        }
      } else if (status === 'in-progress') {
        completed = Math.random() > 0.5;
        if (completed && item.documentRequired) {
          document = {
            fileName: `${item.name.toLowerCase().replace(/ /g, '_')}_employee${i}.pdf`,
            uploadDate: new Date().toISOString().split('T')[0],
            status: Math.random() > 0.5 ? 'approved' as DocumentStatus : 'pending' as DocumentStatus
          };
        }
      }
      
      return { ...item, completed, document };
    });
    
    // Current date + random days between 1-30 for joining date
    const joinDate = new Date();
    joinDate.setDate(joinDate.getDate() + Math.floor(Math.random() * 30) + 1);
    
    records.push({
      id: `${i}`,
      employeeId: `EMP${(10000 + i).toString().padStart(5, '0')}`,
      name: `Employee ${i}`,
      email: `employee${i}@bytesplash.edu`,
      department,
      designation,
      joiningDate: joinDate.toISOString().split('T')[0],
      status,
      items
    });
  }
  
  return records;
};

const ONBOARDING_DATA = generateOnboardingData();

const OnboardingChecklist = ({ 
  record, 
  onUpdate, 
  isSubmittable = false, 
  onSubmitForApproval 
}: { 
  record: OnboardingRecord, 
  onUpdate: (id: string, items: OnboardingItem[]) => void,
  isSubmittable?: boolean,
  onSubmitForApproval?: (id: string) => void
}) => {
  const [localItems, setLocalItems] = useState<OnboardingItem[]>([...record.items]);
  const fileInputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});
  const { toast } = useToast();
  
  const handleToggleItem = (itemId: string) => {
    const updatedItems = localItems.map(item => 
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
  };
  
  const handleFileChange = (itemId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // In a real app, you would upload the file to a server here
    const updatedItems = localItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          completed: true,
          document: {
            fileName: file.name,
            uploadDate: new Date().toISOString().split('T')[0],
            status: 'pending' as DocumentStatus
          }
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Uploaded",
      description: `${file.name} has been uploaded and is pending approval.`
    });
  };
  
  const handleDeleteDocument = (itemId: string) => {
    const updatedItems = localItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          completed: false,
          document: null
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Removed",
      description: "The document has been removed."
    });
  };
  
  const triggerFileInput = (itemId: string) => {
    fileInputRefs.current[itemId]?.click();
  };
  
  const handleApproveDocument = (itemId: string) => {
    const updatedItems = localItems.map(item => {
      if (item.id === itemId && item.document) {
        return {
          ...item,
          document: {
            ...item.document,
            status: 'approved' as DocumentStatus,
            approvalDate: new Date().toISOString().split('T')[0],
            approvedBy: 'Current Admin User'
          }
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Approved",
      description: "The document has been approved successfully."
    });
  };
  
  const handleRejectDocument = (itemId: string) => {
    const updatedItems = localItems.map(item => {
      if (item.id === itemId && item.document) {
        return {
          ...item,
          document: {
            ...item.document,
            status: 'rejected' as DocumentStatus
          }
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Rejected",
      description: "The document has been rejected."
    });
  };
  
  const completedCount = localItems.filter(item => item.completed).length;
  const totalCount = localItems.length;
  const progress = Math.round((completedCount / totalCount) * 100);
  
  const canSubmitForApproval = isSubmittable && 
    localItems.filter(item => item.documentRequired && (!item.document || item.document.status === 'rejected')).length === 0 &&
    record.status !== 'approval_pending' && 
    record.status !== 'completed';
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <div className="flex-1">
          <h3 className="text-sm font-medium">Onboarding Progress: {progress}%</h3>
          <div className="w-full h-2 bg-gray-100 rounded-full mt-1">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-bytesplash-blue rounded-full"
            />
          </div>
        </div>
        <StatusBadge status={record.status} />
      </div>
      
      <div className="space-y-2">
        {localItems.map(item => (
          <div key={item.id} className="p-3 rounded-md border hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div 
                  className={`h-5 w-5 rounded-full flex items-center justify-center cursor-pointer ${
                    item.completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}
                  onClick={() => !item.documentRequired && handleToggleItem(item.id)}
                >
                  {item.completed && <CheckCircle size={16} />}
                </div>
                <span className={item.completed ? 'text-gray-500' : 'font-medium'}>
                  {item.name}
                  {item.documentRequired && (
                    <span className="ml-2 text-xs text-blue-500">(Requires Document)</span>
                  )}
                </span>
              </div>
              
              {item.documentRequired && (
                <div className="flex items-center gap-2">
                  {item.document ? (
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        item.document.status === 'approved' ? 'success' :
                        item.document.status === 'rejected' ? 'destructive' : 'outline'
                      }>
                        {item.document.status === 'approved' ? 'Approved' :
                         item.document.status === 'rejected' ? 'Rejected' : 'Pending Approval'}
                      </Badge>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteDocument(item.id)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Remove document</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => triggerFileInput(item.id)}>
                      <Upload className="h-3 w-3 mr-1" /> Upload
                    </Button>
                  )}
                  
                  {/* Hidden file input */}
                  <input
                    type="file"
                    className="hidden"
                    ref={ref => fileInputRefs.current[item.id] = ref}
                    onChange={(e) => handleFileChange(item.id, e)}
                  />
                </div>
              )}
            </div>
            
            {item.document && (
              <div className="mt-2 text-sm">
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                  <div className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700 truncate max-w-[200px]">{item.document.fileName}</span>
                    <span className="text-xs text-gray-500">Uploaded on {item.document.uploadDate}</span>
                  </div>
                  
                  {/* Admin actions for document approval - would be shown based on user role */}
                  {item.document.status === 'pending' && (
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-green-600" onClick={() => handleApproveDocument(item.id)}>
                        <UserCheck className="h-3 w-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => handleRejectDocument(item.id)}>
                        <X className="h-3 w-3 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {canSubmitForApproval && (
        <div className="pt-4 border-t mt-4">
          <Button 
            className="w-full"
            onClick={() => onSubmitForApproval && onSubmitForApproval(record.id)}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Submit for MASTERADMIN Approval
          </Button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            All required documents have been uploaded. You can now submit this onboarding process for final approval.
          </p>
        </div>
      )}
    </div>
  );
};

const EmployeeOnboarding = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState<OnboardingRecord[]>(ONBOARDING_DATA);
  const [selectedRecord, setSelectedRecord] = useState<OnboardingRecord | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const recordsPerPage = 8;
  
  const filteredRecords = records.filter(
    (record) => {
      return record.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.designation.toLowerCase().includes(searchQuery.toLowerCase());
    }
  );
  
  const pendingRecords = filteredRecords.filter(r => r.status === 'pending');
  const inProgressRecords = filteredRecords.filter(r => r.status === 'in-progress');
  const completedRecords = filteredRecords.filter(r => r.status === 'completed');
  const approvalPendingRecords = filteredRecords.filter(r => r.status === 'approval_pending');
  
  const handleOpenDialog = (record: OnboardingRecord) => {
    setSelectedRecord(record);
    setIsDialogOpen(true);
  };
  
  const handleUpdateItems = (recordId: string, updatedItems: OnboardingItem[]) => {
    const updatedRecords = records.map(record => {
      if (record.id === recordId) {
        // Calculate new status based on completed items
        const completedCount = updatedItems.filter(item => item.completed).length;
        const totalCount = updatedItems.length;
        
        let newStatus = record.status;
        
        if (record.status !== 'approval_pending' && record.status !== 'completed' && record.status !== 'rejected') {
          if (completedCount === 0) {
            newStatus = 'pending';
          } else if (completedCount === totalCount) {
            newStatus = 'completed';
          } else {
            newStatus = 'in-progress';
          }
        }
        
        return { ...record, items: updatedItems, status: newStatus };
      }
      return record;
    });
    
    setRecords(updatedRecords);
    
    // Also update selectedRecord if it's the one being modified
    if (selectedRecord && selectedRecord.id === recordId) {
      const updatedRecord = updatedRecords.find(r => r.id === recordId);
      if (updatedRecord) {
        setSelectedRecord(updatedRecord);
      }
    }
    
    toast({
      title: "Onboarding Updated",
      description: "Onboarding information has been updated successfully."
    });
  };
  
  const handleSubmitForApproval = (recordId: string) => {
    const updatedRecords = records.map(record => {
      if (record.id === recordId) {
        return { ...record, status: 'approval_pending' as OnboardingStatus };
      }
      return record;
    });
    
    setRecords(updatedRecords);
    
    // Also update selectedRecord if it's the one being modified
    if (selectedRecord && selectedRecord.id === recordId) {
      const updatedRecord = updatedRecords.find(r => r.id === recordId);
      if (updatedRecord) {
        setSelectedRecord(updatedRecord);
      }
    }
    
    setIsDialogOpen(false);
    
    toast({
      title: "Submitted for Approval",
      description: "The onboarding process has been submitted to MASTERADMIN for approval."
    });
  };
  
  const handleApproveRecord = (recordId: string) => {
    const updatedRecords = records.map(record => {
      if (record.id === recordId) {
        // Mark all items as approved
        const updatedItems = record.items.map(item => {
          if (item.document) {
            return {
              ...item,
              document: {
                ...item.document,
                status: 'approved' as DocumentStatus,
                approvalDate: new Date().toISOString().split('T')[0],
                approvedBy: 'MASTERADMIN'
              }
            };
          }
          return item;
        });
        
        return { 
          ...record, 
          status: 'completed' as OnboardingStatus,
          items: updatedItems
        };
      }
      return record;
    });
    
    setRecords(updatedRecords);
    
    toast({
      title: "Onboarding Approved",
      description: "The onboarding process has been approved successfully."
    });
  };
  
  const handleRejectRecord = (recordId: string) => {
    const updatedRecords = records.map(record => {
      if (record.id === recordId) {
        return { ...record, status: 'rejected' as OnboardingStatus };
      }
      return record;
    });
    
    setRecords(updatedRecords);
    
    toast({
      title: "Onboarding Rejected",
      description: "The onboarding process has been rejected."
    });
  };
  
  const getCurrentRecords = (status: string) => {
    let currentRecords;
    
    if (status === 'pending') {
      currentRecords = pendingRecords;
    } else if (status === 'in-progress') {
      currentRecords = inProgressRecords;
    } else if (status === 'completed') {
      currentRecords = completedRecords;
    } else if (status === 'approval_pending') {
      currentRecords = approvalPendingRecords;
    } else {
      currentRecords = filteredRecords;
    }
    
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return currentRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  };
  
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Employee Onboarding" subtitle="Manage employee onboarding process" />
      
      <div className="flex-1 p-6 animate-fade-in">
        <Tabs defaultValue="all" className="space-y-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <TabsList>
              <TabsTrigger value="all">All ({filteredRecords.length})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({pendingRecords.length})</TabsTrigger>
              <TabsTrigger value="in-progress">In Progress ({inProgressRecords.length})</TabsTrigger>
              <TabsTrigger value="approval_pending">Approval Pending ({approvalPendingRecords.length})</TabsTrigger>
              <TabsTrigger value="completed">Completed ({completedRecords.length})</TabsTrigger>
            </TabsList>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search..."
                  className="pl-8 w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Button variant="outline" size="icon" className="h-10 w-10">
                <Filter size={16} />
              </Button>
              
              <Button onClick={() => navigate('/employees/register')}>
                <Plus className="mr-2 h-4 w-4" />
                New Employee
              </Button>
            </div>
          </div>
          
          <TabsContent value="all">
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getCurrentRecords('all').length > 0 ? (
                      getCurrentRecords('all').map(record => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.employeeId}</TableCell>
                          <TableCell>{record.name}</TableCell>
                          <TableCell>{record.department}</TableCell>
                          <TableCell>{record.designation}</TableCell>
                          <TableCell>{record.joiningDate}</TableCell>
                          <TableCell>
                            <StatusBadge status={
                              record.status === 'approval_pending' 
                                ? 'Pending Approval' 
                                : record.status
                            } />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-full h-2 bg-gray-100 rounded-full">
                                <div 
                                  className="h-full bg-bytesplash-blue rounded-full" 
                                  style={{ 
                                    width: `${Math.round(
                                      (record.items.filter(item => item.completed).length / record.items.length) * 100
                                    )}%` 
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500">
                                {record.items.filter(item => item.completed).length}/{record.items.length}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button size="sm" variant="outline" onClick={() => handleOpenDialog(record)}>
                                Manage
                              </Button>
                              
                              {record.status === 'approval_pending' && (
                                <>
                                  <Button size="sm" variant="outline" className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100" onClick={() => handleApproveRecord(record.id)}>
                                    <UserCheck className="h-3 w-3 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="outline" className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100" onClick={() => handleRejectRecord(record.id)}>
                                    <X className="h-3 w-3 mr-1" /> Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <Search className="h-10 w-10 mb-2" />
                            <p className="text-lg">No records found</p>
                            <p className="text-sm">Try adjusting your search query</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                
                <div className="p-4 flex justify-center border-t">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => paginate(currentPage - 1)} 
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      
                      {Array.from({ length: Math.ceil(filteredRecords.length / recordsPerPage) }).map((_, i) => (
                        <PaginationItem key={i}>
                          <PaginationLink 
                            onClick={() => paginate(i + 1)}
                            isActive={currentPage === i + 1}
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => paginate(currentPage + 1)} 
                          className={currentPage === Math.ceil(filteredRecords.length / recordsPerPage) ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="pending">
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getCurrentRecords('pending').length > 0 ? (
                      getCurrentRecords('pending').map(record => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.employeeId}</TableCell>
                          <TableCell>{record.name}</TableCell>
                          <TableCell>{record.department}</TableCell>
                          <TableCell>{record.designation}</TableCell>
                          <TableCell>{record.joiningDate}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-full h-2 bg-gray-100 rounded-full">
                                <div 
                                  className="h-full bg-bytesplash-blue rounded-full" 
                                  style={{ 
                                    width: `${Math.round(
                                      (record.items.filter(item => item.completed).length / record.items.length) * 100
                                    )}%` 
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500">
                                {record.items.filter(item => item.completed).length}/{record.items.length}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => handleOpenDialog(record)}>
                              Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <CheckCircle className="h-10 w-10 mb-2 text-green-500" />
                            <p className="text-lg">No pending onboarding tasks</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="in-progress">
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getCurrentRecords('in-progress').length > 0 ? (
                      getCurrentRecords('in-progress').map(record => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.employeeId}</TableCell>
                          <TableCell>{record.name}</TableCell>
                          <TableCell>{record.department}</TableCell>
                          <TableCell>{record.designation}</TableCell>
                          <TableCell>{record.joiningDate}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-full h-2 bg-gray-100 rounded-full">
                                <div 
                                  className="h-full bg-bytesplash-blue rounded-full" 
                                  style={{ 
                                    width: `${Math.round(
                                      (record.items.filter(item => item.completed).length / record.items.length) * 100
                                    )}%` 
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500">
                                {record.items.filter(item => item.completed).length}/{record.items.length}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => handleOpenDialog(record)}>
                              Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <Clock className="h-10 w-10 mb-2 text-yellow-500" />
                            <p className="text-lg">No in-progress onboarding tasks</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="approval_pending">
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Documents</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getCurrentRecords('approval_pending').length > 0 ? (
                      getCurrentRecords('approval_pending').map(record => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.employeeId}</TableCell>
                          <TableCell>{record.name}</TableCell>
                          <TableCell>{record.department}</TableCell>
                          <TableCell>{record.designation}</TableCell>
                          <TableCell>{record.joiningDate}</TableCell>
                          <TableCell>
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                              {record.items.filter(item => item.document).length} Documents Pending Review
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button size="sm" variant="outline" onClick={() => handleOpenDialog(record)}>
                                Review
                              </Button>
                              <Button size="sm" variant="outline" className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100" onClick={() => handleApproveRecord(record.id)}>
                                <UserCheck className="h-3 w-3 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100" onClick={() => handleRejectRecord(record.id)}>
                                <X className="h-3 w-3 mr-1" /> Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <CheckCircle className="h-10 w-10 mb-2 text-green-500" />
                            <p className="text-lg">No onboarding processes waiting for approval</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="completed">
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Documents</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getCurrentRecords('completed').length > 0 ? (
                      getCurrentRecords('completed').map(record => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.employeeId}</TableCell>
                          <TableCell>{record.name}</TableCell>
                          <TableCell>{record.department}</TableCell>
                          <TableCell>{record.designation}</TableCell>
                          <TableCell>{record.joiningDate}</TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              All Documents Approved
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => handleOpenDialog(record)}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <AlertTriangle className="h-10 w-10 mb-2 text-amber-500" />
                            <p className="text-lg">No completed onboarding tasks</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Onboarding Management Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Onboarding Process</DialogTitle>
            <DialogDescription>
              {selectedRecord && (
                <>
                  <span className="font-medium">{selectedRecord.name}</span> - {selectedRecord.designation} at {selectedRecord.department}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedRecord && (
              <OnboardingChecklist 
                record={selectedRecord} 
                onUpdate={handleUpdateItems}
                isSubmittable={true}
                onSubmitForApproval={handleSubmitForApproval}
              />
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setIsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeOnboarding;
